import { useEffect } from "react";
import Input from "../components/shared/Input";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUser } from "../hooks/useQueries";

export default function UserProfile() {
  const {
    user,
    updateUser,
    salesEngineers,
    userPreferences,
    updateUserPreferences,
  } = useUser();

  const validationSchema = yup.object().shape({
    title: yup.string().oneOf(["Mr", "Mrs", "Miss", "Ms", "Dr"], "Required"),
    firstName: yup.string().required("Required"),
    lastName: yup.string().required("Required"),
    company: yup.string(),
    jobTitle: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    getFieldState,
    getValues,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { title: user.title, firstName: user.firstName, lastName: user.lastName, company: user.company, jobTitle: user.jobTitle },
  });

  const onSubmit = (data, e) => {
    updateUser(data);
  };

  const onError = (errors, e) => console.log(errors, e);

  // ---- Project Details defaults (pre-fill new projects) ----
  const {
    register: registerPrefs,
    handleSubmit: handleSubmitPrefs,
    reset: resetPrefs,
    setValue: setValuePrefs,
  } = useForm({
    defaultValues: {
      applicationEngineer: { name: "", telephone: "", email: "" },
      salesEngineer: { name: "", telephone: "", email: "" },
      salesManager: { name: "", telephone: "", email: "" },
    },
  });

  // Load saved defaults once they arrive
  useEffect(() => {
    if (userPreferences) {
      resetPrefs({
        applicationEngineer: userPreferences.applicationEngineer ?? {
          name: "",
          telephone: "",
          email: "",
        },
        salesEngineer: userPreferences.salesEngineer ?? {
          name: "",
          telephone: "",
          email: "",
        },
        salesManager: userPreferences.salesManager ?? {
          name: "",
          telephone: "",
          email: "",
        },
      });
    }
  }, [userPreferences, resetPrefs]);

  // Picking a Sales Engineer auto-fills their Tel + Email from the users list
  const handleSalesEngineerChange = (name) => {
    const selected = salesEngineers?.find((u) => u.name === name);
    setValuePrefs("salesEngineer.telephone", selected?.telephone ?? "");
    setValuePrefs("salesEngineer.email", selected?.email ?? "");
  };

  const onSubmitPrefs = (data) => {
    updateUserPreferences(data);
  };

  const titles = [
    { data: "Please select", value: "" },
    { data: "Mr", value: "Mr" },
    { data: "Mrs", value: "Mrs" },
    { data: "Miss", value: "Miss" },
    { data: "Ms", value: "Ms" },
    { data: "Dr", value: "Dr" },
  ];

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="flex min-h-full flex-col justify-center py-4">
          {/* <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="text-center text-2xl tracking-tight text-gray-700 font-medium ">Create an account</h2>
          </div> */}

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-6 px-6 shadow sm:rounded-lg">
              <div className="space-y-4">
                <Input label="Title" elementtype="select" type="text" options={titles} {...register("title")} error={errors.title} />
                {errors.title && <span className="text-red-500 text-xs">{errors.title.message}</span>}

                <Input label="First name" elementtype="input" type="text" {...register("firstName")} error={errors.firstName} />
                {errors.firstName && <span className="text-red-500 text-xs">{errors.firstName.message}</span>}

                <Input label="Last name" elementtype="input" type="text" {...register("lastName")} error={errors.lastName} />
                {errors.lastName && <span className="text-red-500 text-xs">{errors.lastName.message}</span>}

                <Input label="Company" elementtype="input" type="text" {...register("company")} />

                <Input label="Job title" elementtype="input" type="text" {...register("jobTitle")} />

                <div>
                  <button
                    type="submit"
                    className="flex w-full justify-center rounded-md bg-primary py-2 px-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <form onSubmit={handleSubmitPrefs(onSubmitPrefs)}>
        <div className="flex min-h-full flex-col justify-center py-4">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-6 px-6 shadow sm:rounded-lg">
              <h3 className="text-base font-semibold text-gray-900">
                Project Details defaults
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                These pre-fill the Project Details of every new project.
              </p>

              <div className="space-y-4">
                {/* Application Engineer */}
                <h4 className="text-sm font-semibold text-gray-700">
                  Application Engineer
                </h4>
                <Input label="Name" elementtype="input" type="text" {...registerPrefs("applicationEngineer.name")} />
                <Input label="Tel/Mob No." elementtype="input" type="text" {...registerPrefs("applicationEngineer.telephone")} />
                <Input label="Email" elementtype="input" type="text" {...registerPrefs("applicationEngineer.email")} />

                {/* Sales Engineer */}
                <h4 className="text-sm font-semibold text-gray-700">
                  Sales Engineer
                </h4>
                <Input
                  label="Name"
                  elementtype="select"
                  addBlankOption={true}
                  type="text"
                  options={salesEngineers.map(function (se) {
                    return { value: se.name, data: se.name };
                  })}
                  {...registerPrefs("salesEngineer.name", {
                    onChange: (e) => handleSalesEngineerChange(e.target.value),
                  })}
                />
                <Input label="Tel/Mob No." elementtype="input" type="text" {...registerPrefs("salesEngineer.telephone")} />
                <Input label="Email" elementtype="input" type="text" {...registerPrefs("salesEngineer.email")} />

                {/* Sales Manager */}
                <h4 className="text-sm font-semibold text-gray-700">
                  Sales Manager
                </h4>
                <Input label="Name" elementtype="input" type="text" {...registerPrefs("salesManager.name")} />
                <Input label="Tel/Mob No." elementtype="input" type="text" {...registerPrefs("salesManager.telephone")} />
                <Input label="Email" elementtype="input" type="text" {...registerPrefs("salesManager.email")} />

                <div>
                  <button
                    type="submit"
                    className="flex w-full justify-center rounded-md bg-primary py-2 px-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                  >
                    Save defaults
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
